import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import dayjs from 'utils/dayjsConfig';

const EMPTY_VALUE = '—';

const formatNullableValue = (value) => {
    if (value == null || value === '') {
        return EMPTY_VALUE;
    }
    return String(value);
};

const renderCreatedAtCell = ({ getValue }) => {
    const value = getValue();
    if (!value) {
        return <span className="text-muted-foreground">{EMPTY_VALUE}</span>;
    }
    return dayjs(value).format('MM/DD/YYYY h:mm a');
};

const renderNullableCell = ({ getValue }) => {
    const formatted = formatNullableValue(getValue());
    if (formatted === EMPTY_VALUE) {
        return <span className="text-muted-foreground">{EMPTY_VALUE}</span>;
    }
    return formatted;
};

const MemoizedSubstitutionRuleEventRow = React.memo(({ row }) => (
    <TableRow>
        {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} className="whitespace-normal break-words text-left">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
        ))}
    </TableRow>
));

MemoizedSubstitutionRuleEventRow.displayName = 'MemoizedSubstitutionRuleEventRow';

/**
 * @param {Object} props
 * @param {Array<Record<string, unknown>>} [props.data=[]]
 * @param {boolean} [props.isLoading=false]
 * @param {boolean} [props.isFetching=false]
 * @param {string} [props.emptyMessage='No substitution rule events found.']
 */
export function SubstitutionRuleEventsTable({
    data = [],
    emptyMessage = 'No substitution rule events found.',
    isFetching = false,
    isLoading = false,
}) {
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'createdAt',
                cell: renderCreatedAtCell,
                header: 'Created At',
            },
            {
                accessorKey: 'originalExternalSku',
                cell: renderNullableCell,
                header: 'Original SKU',
            },
            {
                accessorKey: 'eventType',
                cell: renderNullableCell,
                header: 'Event Type',
            },
            {
                accessorKey: 'eventMessage',
                cell: renderNullableCell,
                header: 'Event Message',
            },
            {
                accessorKey: 'authUserDisplayName',
                cell: renderNullableCell,
                header: 'User Name',
            },
        ],
        [],
    );

    const table = useReactTable({
        columns,
        data: tableData,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row, index) => (row?.id != null ? String(row.id) : String(index)),
    });

    return (
        <div className="flex flex-1 flex-col">
            {isFetching && !isLoading ? (
                <div className="mb-2 h-1 w-full overflow-hidden rounded bg-muted">
                    <div className="h-full w-1/3 animate-pulse bg-primary/40" />
                </div>
            ) : null}
            <div className="flex min-h-[240px] flex-1 flex-col">
                {isLoading ? (
                    <div className="flex flex-1 items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Loading substitution rule events…</span>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-md border pb-2">
                        <Table className="w-full table-fixed">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead
                                                key={header.id}
                                                className="whitespace-normal break-words text-left"
                                            >
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table
                                        .getRowModel()
                                        .rows.map((row) => <MemoizedSubstitutionRuleEventRow key={row.id} row={row} />)
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    );
}

SubstitutionRuleEventsTable.displayName = 'SubstitutionRuleEventsTable';
